import { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import toast from 'react-hot-toast'
import { Container, Section, Heading, Text, PricingCard } from '.'
import { billingApi } from '../api'
import type { PlanResponse } from '../api'
import { useAuth } from '../lib/auth'

function formatPrice(price: number | string): string {
  const num = typeof price === 'string' ? parseFloat(price) : price
  return num === 0 ? '$0' : `$${num}`
}

function PricingCardSkeleton() {
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-8 animate-pulse">
      <div className="h-6 bg-slate-200 rounded w-20 mb-2"></div>
      <div className="h-10 bg-slate-200 rounded w-24 mb-4"></div>
      <div className="h-4 bg-slate-200 rounded w-full mb-6"></div>
      <div className="space-y-3">
        {[1, 2, 3, 4, 5].map(i => (
          <div key={i} className="h-4 bg-slate-200 rounded w-3/4"></div>
        ))}
      </div>
      <div className="h-12 bg-slate-200 rounded w-full mt-8"></div>
    </div>
  )
}

export function PricingSection() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const [loadingPlan, setLoadingPlan] = useState<string | null>(null)
  const [plans, setPlans] = useState<PlanResponse[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    async function fetchPlans() {
      try {
        const fetchedPlans = await billingApi.getPlans()
        setPlans(fetchedPlans)
      } catch (err) {
        console.error('Failed to fetch plans:', err)
        toast.error('Failed to load pricing plans')
      } finally {
        setLoading(false)
      }
    }
    fetchPlans()
  }, [])

  const handlePlanClick = async (planName: string) => {
    // Free plan - just navigate to signup
    if (planName === 'free') {
      if (user) {
        toast.success('You already have an account!')
        navigate('/dashboard')
      } else {
        navigate('/signup')
      }
      return
    }

    // Paid plans - need to be logged in first
    if (!user) {
      // Store selected plan and redirect to signup
      localStorage.setItem('falcon_selected_plan', planName.toLowerCase())
      navigate(`/signup?plan=${planName.toLowerCase()}`)
      return
    }

    // Create checkout session
    setLoadingPlan(planName)
    try {
      const result = await billingApi.createCheckoutSession(planName.toLowerCase())
      // Redirect to Stripe checkout
      window.location.href = result.url
    } catch (err: unknown) {
      const error = err as { response?: { data?: { detail?: string } } }
      toast.error(error.response?.data?.detail || 'Failed to start checkout')
      setLoadingPlan(null)
    }
  }

  return (
    <Section className="relative overflow-hidden bg-slate-100">
      <Container className="relative z-10">
        <div className="space-y-16">
          {/* Section header */}
          <div className="text-center space-y-4 max-w-3xl mx-auto">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary-100 border border-primary-300">
              <span className="text-sm text-primary-800 font-medium">Pricing</span>
            </div>

            <Heading as="h2" className="text-4xl md:text-5xl font-bold">
              <span className="text-slate-900">Simple, transparent</span>
              <br />
              <span className="bg-gradient-to-r from-primary-600 to-accent-500 bg-clip-text text-transparent">
                pricing for everyone
              </span>
            </Heading>

            <Text size="lg" className="text-slate-600">
              Start free, upgrade when you need more. No surprises, no hidden fees.
            </Text>
          </div>

          {/* Pricing cards */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 items-start max-w-6xl mx-auto">
            {loading ? (
              <>
                <PricingCardSkeleton />
                <PricingCardSkeleton />
                <PricingCardSkeleton />
              </>
            ) : (
              plans.map(plan => (
                <PricingCard
                  key={plan.id || plan.name}
                  name={plan.display_name}
                  price={formatPrice(plan.price_monthly)}
                  period={parseFloat(String(plan.price_monthly)) > 0 ? 'mo' : undefined}
                  description={plan.description || ''}
                  features={plan.features}
                  cta={plan.name === 'free' ? 'Start Free' : 'Get Started'}
                  recommended={plan.recommended}
                  badge={plan.badge || undefined}
                  onClick={() => handlePlanClick(plan.name)}
                  isLoading={loadingPlan === plan.name}
                />
              ))
            )}
          </div>

          {/* FAQ callout */}
          <div className="text-center space-y-4 pt-8">
            <div className="max-w-2xl mx-auto space-y-6">
              {/* Trust indicators */}
              <div className="flex flex-wrap justify-center gap-6 text-sm text-slate-600">
                <div className="flex items-center gap-2">
                  <svg
                    className="w-5 h-5 text-green-400"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  <span>No credit card required</span>
                </div>
                <div className="flex items-center gap-2">
                  <svg
                    className="w-5 h-5 text-green-400"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  <span>Cancel anytime</span>
                </div>
                <div className="flex items-center gap-2">
                  <svg
                    className="w-5 h-5 text-green-400"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  <span>60-day free trial</span>
                </div>
              </div>

              {/* Comparison note */}
              <div className="p-6 rounded-xl bg-white border border-slate-200 shadow-sm">
                <div className="space-y-4">
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 p-2 rounded-lg bg-primary-100 border border-primary-200">
                      <svg
                        className="w-6 h-6 text-primary-600"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                    </div>
                    <div className="text-left">
                      <Text className="font-semibold text-slate-900 mb-1">
                        All plans include the same features
                      </Text>
                      <Text className="text-slate-600 text-sm">
                        The only difference is error volume and retention. Unlimited apps and team
                        members on all plans.
                      </Text>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3 pt-2 text-sm text-slate-700">
                    <div className="flex items-center gap-2">
                      <svg
                        className="w-4 h-4 text-green-400 flex-shrink-0"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                      <span>Email alerts</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <svg
                        className="w-4 h-4 text-green-400 flex-shrink-0"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                      <span>Error tracking</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <svg
                        className="w-4 h-4 text-green-400 flex-shrink-0"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                      <span>Uptime monitoring</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <svg
                        className="w-4 h-4 text-green-400 flex-shrink-0"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                      <span>Slack & webhooks</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <svg
                        className="w-4 h-4 text-green-400 flex-shrink-0"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                      <span>Custom metrics</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <svg
                        className="w-4 h-4 text-green-400 flex-shrink-0"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                      <span>API access</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Enterprise callout */}
              <Text className="text-slate-500 text-sm">
                Need higher error limits or longer retention?{' '}
                <Link to="/contact" className="text-primary-600 hover:text-primary-700 underline">
                  Contact us
                </Link>{' '}
                for custom enterprise pricing.
              </Text>
            </div>
          </div>
        </div>
      </Container>
    </Section>
  )
}
